import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import {Button, Modal} from 'antd';

import {ErrorAlert} from '../common/ErrorAlert';
import {
  Attachment,
  ChecklistSummaries,
  ChecklistSummary,
  ErrorResponse,
  Returnal,
  ReturnalDocumentSendout,
  ReturnalDocumentType,
} from '../api/dtos/dtos';
import locales from '../locales';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import ReturnDetailsEditor from './ReturnDetailsEditor';
import * as paths from '../paths';
import {IdPageUrlParams} from '../paths';
import ButtonBar from '../common/ButtonBar';
import ReturnalTransportLetterEditor from './ReturnalTransportLetterEditor';
import {setAppHeaderSubtitle} from '../app/AppHeader';
import {showSaveConfirm, showSaveSuccessNotification} from "../utils/dialogs";
import accountProvider from "../account/AccountProvider";
import {idFromPathParams} from "../utils/router-utils";
import ChecklistSummaryComponent from "../checklist/ChecklistSummaryComponent";
import DocumentSendoutSummaryComponent from "../document-sendout/DocumentSendoutSummaryComponent";
import DocumentUploader from "../document-sendout/DocumentUploader";
import {openPdf} from "../utils/file-utils";

interface ReturnDetailsPageState {
  editable: boolean;
  returnal?: Returnal;
  editedReturnal?: Returnal;
  error?: ErrorResponse;
  transportFormVisible: boolean;
  checklistSummary?: ChecklistSummary;
  documents?: ReturnalDocumentSendout[];
}

class ReturnDetailsPage extends React.Component<RouteComponentProps<IdPageUrlParams>, ReturnDetailsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  constructor(props: RouteComponentProps<{}>) {
    super(props);
    this.state = {
      editable: false,
      transportFormVisible: false,
    };
  }

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {editable, editedReturnal, documents, error, transportFormVisible, checklistSummary} = this.state;
    const isAuthorised = accountProvider.isAdmin() || accountProvider.isSales();
    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        {editedReturnal && (
          <Modal
            title={locales.transport_form}
            visible={transportFormVisible}
            footer={null}
            onCancel={this.onTransportFormCancel}
            destroyOnClose={true}
          >
            <ReturnalTransportLetterEditor
              returnalId={editedReturnal.id!}
              onClose={this.onTransportFormCancel}
            />
          </Modal>
        )}

        {isAuthorised && <ButtonBar style={{marginBottom: 24}}>

            <Button
                icon="file-pdf"
                onClick={this.onPrintClick}
                disabled={editable}
            >{locales.returnal_form}</Button>

            <Button
                icon="solution"
                onClick={this.onPrintTransportClick}
                disabled={editable}
            >{locales.transport_form}</Button>

            <Button
                type="primary"
                icon="edit"
                onClick={this.onEditClick}
                disabled={editable}
            >{locales.edit}</Button>
        </ButtonBar>}

        <ReturnDetailsEditor
          mode="edit"
          returnal={editedReturnal}
          checklistSummary={checklistSummary}
          showChecklist={true}
          onReturnalChange={this.onReturnalChange}
          editable={editable}
          onSubmit={this.onFormSubmit}
          onCancel={this.onFormCancel}
          onChecklistAdd={this.onCreateChecklistClick}
          onChecklistEdit={this.onEditChecklistClick}
        />

        {checklistSummary &&
            <ChecklistSummaryComponent
                summary={checklistSummary}
                onSendoutChecklists={this.onSendoutChecklists}
                style={{marginBottom: 24}}
            />
        }

        {documents &&
            <DocumentSendoutSummaryComponent
                documents={documents}
                onSendoutDocument={this.onSendDocuments}
                style={{marginBottom: 24}}
            />
        }


        {editedReturnal?.id &&
            <DocumentUploader
                id={editedReturnal.id}
                onDelete={this.onAttachmentDelete}
                onLoad={this.loadAttachments}
                action={this.onAttachmentUpload}
            />}


        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }


  private loadAttachments = (id: number): Promise<Attachment[]> => {
    return furoApi.returns.getAttachments(id);
  }

  private onAttachmentDelete = (id: number, attachmentId: number): Promise<void> => {
    return furoApi.returns.deleteAttachment(id, attachmentId);
  }

  private onAttachmentUpload = (id: number): string => {
    return furoApi.returns.uploadAttachmentsUrl(id);
  }

  private onPrintClick = () => {
    const {returnal} = this.state;
    if (returnal) {
      const promise = furoApi.document.getReturnalDocument(returnal.id!, ReturnalDocumentType.RETURNAL_REPORT)
        .then(openPdf)
        .catch(this.onApiError);
      withLoadingOverlay(promise, this.loadingHandler);
    }
  };

  private onPrintTransportClick = () => {
    this.setState({transportFormVisible: true,});
  };

  private reload = () => {
    this.loadReturnal();
    this.loadChecklistSummary();
    this.loadDocuments();
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };

  private loadReturnal = () => {
    const returnalID = idFromPathParams(this.props);
    const promise = furoApi.returns.get(returnalID)
      .then(this.onLoadReturnalSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadReturnalSuccess = (response: Returnal) => {
    this.setState({
      returnal: response,
      editedReturnal: response,
      error: undefined,
    });
    setAppHeaderSubtitle(this.props, response.idString);
  };

  private loadChecklistSummary = () => {
    const returnalID = idFromPathParams(this.props);
    const promise = furoApi.checklist.getReturnalChecklists(returnalID)
      .then(this.onLoadChecklistSummaries)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadChecklistSummaries = (response: ChecklistSummaries) => {
    this.setState({
      checklistSummary: response.returnal,
      error: undefined,
    });
  };

  private loadDocuments = () => {
    const returnalId = idFromPathParams(this.props);
    const promise = furoApi.document.getReturnalDocumentSendouts(returnalId)
      .then(this.onLoadDocuments)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onLoadDocuments = (response: ReturnalDocumentSendout[]) => {
    this.setState({
      documents: response,
      error: undefined,
    });
  };

  private onSendDocuments = () => {
    this.props.history.push(paths.viewReturnalDocuments(this.state.returnal?.id));
  };

  private onReturnalChange = (returnal: Returnal) => {
    this.setState({editedReturnal: returnal,});
  };

  private onEditClick = () => {
    this.setEditable(true);
  };

  private onFormCancel = () => {
    this.setEditable(false);
    this.setState({editedReturnal: this.state.returnal,});
  };

  private setEditable = (editable: boolean) => {
    this.setState({editable,});
  };

  private onFormSubmit = () => {
    showSaveConfirm(this.saveFormValues);
  };

  private saveFormValues = () => {
    const {editedReturnal} = this.state;
    if (!editedReturnal) {
      return;
    }

    const promise = furoApi.returns.save(editedReturnal)
      .then(() => {
        showSaveSuccessNotification();
        this.reload();
        this.setEditable(false);
      })
      .catch((error: ErrorResponse) => {
        this.setState({error});
      });

    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onTransportFormCancel = () => {
    this.setState({
      transportFormVisible: false,
    });
  };

  private onCreateChecklistClick = (rentalItemId: number) => {
    const {returnal} = this.state;

    this.props.history.push(paths.createChecklistPath({rentalItemId, returnalId: returnal?.id!}))
  };

  private onEditChecklistClick = (checklistId: number) => {
    this.props.history.push(paths.viewChecklistPath(checklistId));
  };

  private onSendoutChecklists = () => {
    this.props.history.push(paths.viewReturnalChecklist(this.state.returnal?.id));
  };
}

export default ReturnDetailsPage;




// WEBPACK FOOTER //
// ./src/return-details/ReturnDetailsPage.tsx